import * as React from 'react';

import config from '../../config';
import { navigateTop } from '../routing/router-utils';
import { logWithIntercom, DISP_VIEW_NAME, DISP_VIEW_CITY } from '../intercom/intercom-utils';
import { useDispatch } from 'react-redux';
import { setAnonymousUserZipCode, setDispensary, addToCart } from '../actions/cartActions';
import { trackEvent } from '../analytics/tracking';
import BodyWrapper, { LayoutTypes } from '../layout/BodyWrapper';
import WideScreenOnly from '../mediaQuery/WideScreenOnly';
import Cart from '../checkout/Cart';
import Spinner from '../common/Spinner';
import DispensaryHeader from './DispensaryHeader';
import { UserContext } from '../providers/UserProvider';
import { DispensaryContext } from './DispensaryProvider';
import { FilteredProductsContext } from '../products/FilteredProductsProvider';
import PrimaryHeader from '../styleguide/PrimaryHeader';
import BulkDiscountMsg from './BulkDiscountMsg';
import ProductFilterCarousel from './ProductFilterCarousel';
import FilterWithinCategory from '../productFilters/FilterWithinCategory';
import WeightFilter, { WEIGHT_ENABLED_CATEGORIES }  from '../productFilters/WeightFilter';
import BrandFilter from '../productFilters/BrandFilter';
import TypeFilter from '../productFilters/TypeFilter';
import SubCategoryFilterModal from '../productFilters/SubCategoryFilterModal';
import DisplayModal from '../common/DisplayModal';
import AddressPrompt from '../address/AddressPrompt';
import AddedToCartCheck from '../products/AddedToCartCheck';
import ProductListing from './ProductListing';
import SignUpBar from '../footer/SignUpBar';
import ReorderBar from '../footer/ReorderBar';
import Helmet from 'react-helmet';
import FilterIcon from '../../assets/filterIcon2.png';

import PropTypes from 'prop-types';

import styles from './Dispensary.module.css';

const Dispensary = ({
  dispensaryId,
  productCategory,
  scrollToProducts
}) => {
     
  // User for logging
  const { user } = React.useContext(UserContext);

  // non-display name
  const [currentCategory, setCurrentCategory] = React.useState();
  // display name
  const [currentCategoryName, setCurrentCategoryName] = React.useState();   
  
  const dispatch = useDispatch(); 
  
  // Migrating from redux to DispensaryProvider for dispensary info
  const { dispensary:dispensaryInfo, dispensaryNotFound } = React.useContext(DispensaryContext); 

  const { setCategory } = React.useContext(FilteredProductsContext);
  // TODO: Remove! Used by the cart (redux) when address is not set
  const [showAddressModal, setShowAddressModal] = React.useState(false);
  const handleAddressUpdate = (zipCode) => {
    dispatch(setAnonymousUserZipCode(zipCode));
    setShowAddressModal(false)  
  };
  
  // checkmark on add-to-cart
  const [showAddedCheck, setShowAddedCheck] = React.useState();
  const [showFilterModal, setShowFilterModal] = React.useState();

  const filterRef = React.useRef();

  // Initial render
  React.useEffect(() => {
    if (productCategory && !currentCategory) {
      setCurrentCategory(productCategory.name);
      setCurrentCategoryName(productCategory.display_name);
      setCategory(productCategory.display_name);
      if (scrollToProducts) {
        // window.scrollTo(0,0);
        window.setTimeout(() => {
          filterRef.current && filterRef.current.scrollIntoView();
        }, 200);
      }
    }
    // TODO: Migrate consumers to DispensaryProvider
    if (dispensaryInfo) {
      dispatch(setDispensary(dispensaryInfo.id));
    }
    if (dispensaryNotFound) {
      navigateTop('/');
      trackEvent('error_dispensary_not_found');
    }
  }, [dispensaryInfo, dispensaryNotFound, productCategory, currentCategory, setCategory, scrollToProducts, dispatch]);
  // Log dispensary name and city with Intercom
  React.useEffect(() => {
    if (dispensaryInfo?.name && user && user.email)  {
      const logInfo = {};
      logInfo[DISP_VIEW_NAME] = dispensaryInfo.name;
      logInfo[DISP_VIEW_CITY] = dispensaryInfo.location?.city;
      logWithIntercom(null, user.email, null, logInfo);
    }  
  }, [user, dispensaryInfo]);

  // Update on category change
  const updateProductCategory = (category) => {
    if (category.name !== currentCategory) {
      setCurrentCategory(category.name);
      setCurrentCategoryName(category.display_name);
      window.scrollTo(0,0);
      filterRef.current && filterRef.current.scrollIntoView();
    } 
  };

  const handleAddToCart = (Id, quantity)=>{
    setShowAddedCheck(true);
    dispatch(addToCart(Id, null, quantity));
  }
 
  return ( 
    <BodyWrapper pageLayout={LayoutTypes.FullBleed}>
      <Helmet>
        <title>Zip Run - Shop {currentCategoryName || ''}</title>
      </Helmet>
      { dispensaryInfo?.id && currentCategory
        ? <>
            <div className={styles.dispensaryGrid}>
              <div className={styles.dispensaryCol}>
                <div className={styles.dispensaryColLayout}>
                  <DispensaryHeader />
                  <div className={styles.filterCarouselWrap}>
                    {/* scroll to element TODO: this is fragile */}
                    <div ref={filterRef} style={{ width:'1px', position:'relative', top:'-42px'}}></div>
                    <ProductFilterCarousel currentCategory={currentCategory} handleClick={updateProductCategory} />
                  </div>
                  <div className={styles.headerWithFilters}>
                    <PrimaryHeader text={currentCategoryName} />
                    <div className={styles.hdrFilterWrap} onClick={() => setShowFilterModal(true)}>
                      <img className={styles.filterIcon} alt="filter" src={FilterIcon} />
                      Filter
                    </div>
                  </div>
                  { currentCategory === 'flower' &&
                    <BulkDiscountMsg dispensaryId={dispensaryId} />
                  }
                  <div className={styles.productGridWithFilters}>
                    <div className={styles.filterCol}>
                      {/* will collapse if emtpy */}
                      <FilterWithinCategory />
                      { WEIGHT_ENABLED_CATEGORIES.includes(currentCategoryName) &&
                        <WeightFilter startOpen />
                      }
                      <BrandFilter startOpen />
                      <TypeFilter  startOpen />
                    </div>
                    <div className={styles.productGrid}>
                      <ProductListing
                        dispensaryId={dispensaryInfo.id}
                        handleClick={handleAddToCart} />
                    </div>
                  </div>
                </div>
              </div>
              <WideScreenOnly>
                <div className={styles.cartCol}>
                  <Cart
                    dispensaryName={dispensaryInfo.name}
                    handleShowAddressPrompt={() => setShowAddressModal(true)}
                    closeMenuFn={()=>{}} />
                </div>
              </WideScreenOnly>
            </div>
            
            { !config.IS_PREVIEW_MODE &&
              <>
                <SignUpBar />
                <ReorderBar />
              </>
            }

            { showFilterModal &&
              <DisplayModal title={`Filter: ${currentCategoryName}`} closeFn={() => setShowFilterModal(false)}>
                <SubCategoryFilterModal handleClose={() => setShowFilterModal(false)} />
             </DisplayModal>
            }
          </>
        : <Spinner />
      }

      { showAddedCheck && 
        <AddedToCartCheck closeFn={() => { setShowAddedCheck(false) }} />
      }
      { showAddressModal &&  
        <DisplayModal title="Your Address" closeFn={() => setShowAddressModal(false)}>
          <AddressPrompt onComplete={(zipCode) => handleAddressUpdate(zipCode)} />
        </DisplayModal>
      }
    </BodyWrapper>
  )
}

Dispensary.propTypes = {
  /* all via reach router, seemingly undefined initially */
  dispensaryId: PropTypes.string.isRequired,
  productCategory: PropTypes.object.isRequired,
  scrollToProducts: PropTypes.bool
};

export default Dispensary;